import { AfterViewInit, computed, ElementRef, Injectable, OnDestroy, signal, Signal } from '@angular/core';
import { SlideBuilderSectionLayoutConfig, SlideBuilderSectionLayoutType, SlideBuilderWidgetLayoutConfig } from '../../models';


const DEFAULT_GRID_COLUMNS = 12;
const DEFAULT_GRID_GAP = 16;
const MIN_GRID_COLUMNS = 2;
const MAX_GRID_COLUMNS = 24;
const MIN_GRID_ROWS = 1;
const MAX_GRID_ROWS = 100;
const DEFAULT_ROWS = 4;

interface BaseGridWidget {
  id: string;
  widgetLayoutConfig?: SlideBuilderWidgetLayoutConfig;
}

@Injectable()
export abstract class BaseSlideBuilderSectionGridLayoutComponent implements AfterViewInit, OnDestroy {
  public abstract readonly gridLayoutConfig: Signal<SlideBuilderSectionLayoutConfig | null | undefined>;
  protected abstract readonly widgetHeights: Signal<Record<string, number>>;
  protected abstract readonly gridWidgets: Signal<BaseGridWidget[]>;

  protected readonly width = signal(0);
  protected readonly height = signal(0);

  public readonly columns = computed(() => this.clamp(
    this.gridConfig()?.columns ?? DEFAULT_GRID_COLUMNS,
    MIN_GRID_COLUMNS,
    MAX_GRID_COLUMNS
  ));

  public readonly columnGap = computed(() => Math.max(0, this.gridConfig()?.columnGap ?? DEFAULT_GRID_GAP));
  public readonly rowGap = computed(() => Math.max(0, this.gridConfig()?.rowGap ?? DEFAULT_GRID_GAP));

  public readonly columnWidth = computed<number | null>(() => {
    const width = this.width();
    const columns = this.columns();

    if (!width || !columns) {
      return null;
    }

    return (width - (columns - 1) * this.columnGap()) / columns;
  });

  public readonly rowHeight = computed<number | null>(() => {
    const columnWidth = this.columnWidth();

    if (!columnWidth) {
      return null;
    }

    return columnWidth * 9 / 16;
  });

  public readonly gridMinHeight = computed<number | null>(() => {
    const rowHeight = this.rowHeight();
    let rows = this.rows() ?? 0;
    const widgets = this.gridWidgets();

    if (!rowHeight) {
      return null;
    }

    if (!widgets?.length) {
      rows = Math.max(DEFAULT_ROWS, rows);
    }

    const rowGap = this.rowGap();
    const widgetsById = new Map(this.gridWidgets().map(widget => [widget.id, widget]));
    const occupiedRows = Object.entries(this.widgetHeights()).reduce((maximum, [widgetId, height]) => {
      const widget = widgetsById.get(widgetId);
      const widgetRows = Math.max(1, Math.ceil((height + rowGap) / (rowHeight + rowGap)));
      const widgetEndRow = (widget?.widgetLayoutConfig?.row ?? 1) + widgetRows - 1;

      return Math.max(maximum, widgetEndRow);
    }, 1);

    const gridRows = Math.max(rows ?? 0, occupiedRows);

    return gridRows * rowHeight + (gridRows - 1) * rowGap;
  });

  public readonly rows = computed<number | null>(() => this.clamp(
    this.gridConfig()?.rows,
    MIN_GRID_ROWS,
    MAX_GRID_ROWS
  ));

  private readonly gridConfig = computed(() => {
    const config = this.gridLayoutConfig();

    return config?.type === SlideBuilderSectionLayoutType.GRID ? config : null;
  });

  private clamp(value: number | undefined, min: number, max: number): number | null {
    if (value === undefined || value === null) {
      return null;
    }

    return Math.min(Math.max(value, min), max);
  }

  protected readonly resizeObserver = new ResizeObserver(([entry]) => {
    this.width.set(entry.contentRect.width);
    this.height.set(entry.contentRect.height);
  });

  constructor(protected readonly elementRef: ElementRef) { }

  public ngAfterViewInit(): void {
    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  public ngOnDestroy(): void {
    this.resizeObserver.disconnect();
  }
}
